import {
  Divider,
  Link,
  NavigationListItem,
  Typography,
} from '@globalmed/atlas-ui';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './NavigationWithOrganizationsHeader.module.css';
import { CreateOrganizationModal } from '@/features/CreateOrganizationModal';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { useOrganizationReportingAccess } from '@/hooks/useOrganizationReportingAccess';
import { useVisibleOrganizations } from '@/hooks/useVisibleOrganizations';
import { ERouterNames } from '@/types';
import { useOrganizationsStore } from '@/stores/organizations';

/**
 * Header of the organizations navigation panel, shown to super admins and
 * organization admins. Shows the current institution's display name and a
 * collapsible list of organizations, scoped to the ones the current user is
 * allowed to see.
 * @returns The navigation panel header
 */
export const NavigationWithOrganizationsHeader = () => {
  const { t } = useTranslation();
  const currentOrganization = useOrganizationsStore(
    (state) => state.currentOrganization,
  );
  const { visibleOrganizations, isSuperAdmin } = useVisibleOrganizations();
  const { hasAccess: hasOrganizationReportingAccess } =
    useOrganizationReportingAccess();
  const handleOrganizationChange = useOrganizationChange();
  const navigate = useNavigate();
  const location = useLocation();

  const organizationAdminsItem = useMemo(
    () => ({
      key: 'organization-admins',
      icon: 'UsersCheck' as const,
      title: t('nav-panel.organization-admins'),
    }),
    [t],
  );

  const organizationReportingItem = useMemo(
    () => ({
      key: 'organization-reporting',
      icon: 'Integrate' as const,
      title: t('nav-panel.organization-reporting'),
    }),
    [t],
  );

  const organizationItems = useMemo(
    () =>
      visibleOrganizations
        .map((organization) => ({
          key: organization.id,
          title: organization.name,
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [visibleOrganizations],
  );

  const [isOrganizationsOpen, setIsOrganizationsOpen] = useState(true);
  const [isCreateOrganizationOpen, setIsCreateOrganizationOpen] =
    useState(false);
  const [isEditOrganizationOpen, setIsEditOrganizationOpen] = useState(false);

  const displayName = currentOrganization?.name;

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <Typography as="span" className={styles.header}>
          {displayName}
        </Typography>
        <div className={styles.actions}>
          <Link
            preIcon={isOrganizationsOpen ? 'ChevronUp' : 'ChevronDown'}
            aria-label={t('common.expand')}
            aria-expanded={isOrganizationsOpen}
            color="black"
            onClick={() => setIsOrganizationsOpen((prev) => !prev)}
          />
          <Link
            preIcon="SettingsGear"
            aria-label={t('common.settings')}
            color="grey"
            onClick={
              currentOrganization
                ? () => setIsEditOrganizationOpen(true)
                : undefined
            }
          />
        </div>
      </div>
      <div
        className={cn(styles.collapsible, {
          [styles.collapsed]: !isOrganizationsOpen,
        })}
      >
        <div className={styles.collapsibleInner}>
          {isSuperAdmin && (
            <Link
              className={styles.link}
              size="sm"
              preIcon="Plus"
              text={t('nav-panel.create-organization')}
              onClick={() => setIsCreateOrganizationOpen(true)}
            />
          )}
          <ul className={styles.organizations}>
            {organizationItems.map((organization) => (
              <NavigationListItem
                key={organization.key}
                item={organization}
                isActive={currentOrganization?.id === organization.key}
                isExpanded={false}
                activeSubItem={null}
                onItemClick={(clicked) =>
                  void handleOrganizationChange(clicked.key)
                }
                onSubItemClick={() => {}}
                onKeyDown={() => {}}
              />
            ))}
          </ul>
        </div>
      </div>
      <Divider className={styles.divider} />
      {!!currentOrganization && (
        <>
          <ul className={styles.organizationAdmins}>
            <NavigationListItem
              item={organizationAdminsItem}
              isActive={location.pathname === ERouterNames.ORGANIZATION_ADMINS}
              isExpanded={false}
              activeSubItem={null}
              onItemClick={() => navigate(ERouterNames.ORGANIZATION_ADMINS)}
              onSubItemClick={() => {}}
              onKeyDown={() => {}}
            />
            {hasOrganizationReportingAccess && (
              <NavigationListItem
                item={organizationReportingItem}
                isActive={
                  location.pathname === ERouterNames.ORGANIZATION_REPORTING
                }
                isExpanded={false}
                activeSubItem={null}
                onItemClick={() =>
                  navigate(ERouterNames.ORGANIZATION_REPORTING)
                }
                onSubItemClick={() => {}}
                onKeyDown={() => {}}
              />
            )}
          </ul>
          <Divider className={styles.divider} />
        </>
      )}
      <CreateOrganizationModal
        open={isCreateOrganizationOpen}
        setOpen={setIsCreateOrganizationOpen}
      />
      <CreateOrganizationModal
        open={isEditOrganizationOpen}
        setOpen={setIsEditOrganizationOpen}
        isEditMode
      />
    </div>
  );
};
